import React from "react";
import {
  FaCheck,
  FaEye,
  FaPen,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaTimes,
  FaTrashAlt,
  FaUserSlash,
} from "react-icons/fa";

import {
  formatDate,
  getGroupColorClass,
  getInitials,
} from "../utils/inscriptionsUtils";

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
    emerald:
      "border-emerald-100 text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 dark:border-emerald-500/20 dark:text-emerald-300 dark:hover:bg-emerald-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function AdherentMobileCard({
  adherent,
  groupColorMap,
  deleteCandidateId,
  onDetails,
  onEdit,
  onAskDelete,
  onConfirmDelete,
  onCancelDelete,
}) {
  const isDeleting = deleteCandidateId === adherent.id;

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:hidden">
      <div className="flex items-start gap-3">
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-orange-100 to-blue-100 text-sm font-bold text-slate-800 dark:from-orange-500/20 dark:to-blue-500/20 dark:text-white">
          {getInitials(adherent)}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate font-bold text-slate-950 dark:text-white">
            {adherent.prenom} {adherent.nom}
          </h3>

          <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
            {adherent.email || "Email non renseigné"}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${getGroupColorClass(
                groupColorMap,
                adherent.adherent_groupe
              )}`}
            >
              {adherent.adherent_groupe || "Sans groupe"}
            </span>

            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {adherent.tel || "Téléphone non renseigné"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {isDeleting ? (
          <>
            <button
              type="button"
              onClick={() => onConfirmDelete(adherent.id)}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white"
            >
              <FaCheck />
              Confirmer
            </button>

            <button
              type="button"
              onClick={onCancelDelete}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 dark:border-slate-700 dark:text-slate-300"
            >
              <FaTimes />
              Annuler
            </button>
          </>
        ) : (
          <>
            <IconButton
              title="Voir la fiche"
              icon={FaEye}
              tone="blue"
              onClick={() => onDetails(adherent)}
            />
            <IconButton
              title="Modifier"
              icon={FaPen}
              tone="orange"
              onClick={() => onEdit(adherent)}
            />
            <IconButton
              title="Supprimer"
              icon={FaTrashAlt}
              tone="rose"
              onClick={() => onAskDelete(adherent.id)}
            />
          </>
        )}
      </div>
    </article>
  );
}

export default function AdherentsTable({
  loading,
  adherents,
  groupColorMap,
  sortOrder,
  onSortByName,
  onDetails,
  onEdit,
  onAskDelete,
  onConfirmDelete,
  onCancelDelete,
  deleteCandidateId,
}) {
  if (loading) {
    return (
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!adherents.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucun adhérent trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou sélectionnez un autre groupe.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="space-y-3 lg:hidden">
        {adherents.map((adherent) => (
          <AdherentMobileCard
            key={adherent.id}
            adherent={adherent}
            groupColorMap={groupColorMap}
            deleteCandidateId={deleteCandidateId}
            onDetails={onDetails}
            onEdit={onEdit}
            onAskDelete={onAskDelete}
            onConfirmDelete={onConfirmDelete}
            onCancelDelete={onCancelDelete}
          />
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none lg:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-950">
              <tr>
                <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <button
                    type="button"
                    onClick={onSortByName}
                    className="inline-flex items-center gap-2 transition hover:text-slate-950 dark:hover:text-white"
                  >
                    Adhérent
                    {sortOrder === "name_desc" ? (
                      <FaSortAlphaDown />
                    ) : (
                      <FaSortAlphaUp />
                    )}
                  </button>
                </th>
                <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Groupe
                </th>
                <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Contact
                </th>
                <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Licence
                </th>
                <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Naissance
                </th>
                <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {adherents.map((adherent) => {
                const isDeleting = deleteCandidateId === adherent.id;

                return (
                  <tr
                    key={adherent.id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-950"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-orange-100 to-blue-100 text-sm font-bold text-slate-800 dark:from-orange-500/20 dark:to-blue-500/20 dark:text-white">
                          {getInitials(adherent)}
                        </div>

                        <div className="min-w-0">
                          <p className="truncate font-bold text-slate-950 dark:text-white">
                            {adherent.prenom} {adherent.nom}
                          </p>
                          <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                            {adherent.commune || "Commune non renseignée"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getGroupColorClass(
                          groupColorMap,
                          adherent.adherent_groupe
                        )}`}
                      >
                        {adherent.adherent_groupe || "Sans groupe"}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {adherent.email || "—"}
                      </p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {adherent.tel || "—"}
                      </p>
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {adherent.licence_number || "Non renseignée"}
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {formatDate(adherent.date_naissance)}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {isDeleting ? (
                          <>
                            <button
                              type="button"
                              onClick={() => onConfirmDelete(adherent.id)}
                              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                            >
                              <FaCheck />
                              Confirmer
                            </button>

                            <button
                              type="button"
                              onClick={onCancelDelete}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                              <FaTimes />
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            <IconButton
                              title="Voir la fiche"
                              icon={FaEye}
                              tone="blue"
                              onClick={() => onDetails(adherent)}
                            />

                            <IconButton
                              title="Modifier"
                              icon={FaPen}
                              tone="orange"
                              onClick={() => onEdit(adherent)}
                            />

                            <IconButton
                              title="Supprimer"
                              icon={FaTrashAlt}
                              tone="rose"
                              onClick={() => onAskDelete(adherent.id)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}